import {
  gql,
  MutationHookOptions,
  QueryHookOptions,
  useMutation,
  useQuery,
} from "@apollo/client";

import { client } from "components/providers/ApolloClientProvider";

/**
 * MFA STATUS QUERY
 */
export const MFA_STATUS_QUERY = gql`
  query MFAStatus {
    mfaStatus {
      enabled
      setupComplete
      requiresSetup
    }
  }
`;

export interface MFAStatusQueryData {
  mfaStatus: {
    enabled: boolean;
    setupComplete: boolean;
    requiresSetup: boolean;
  };
}

export const useMFAStatusQuery = (
  options?: QueryHookOptions<MFAStatusQueryData>
) => useQuery<MFAStatusQueryData>(MFA_STATUS_QUERY, options);

/**
 * LIST TRUSTED DEVICES QUERY
 */
export const LIST_TRUSTED_DEVICES_QUERY = gql`
  query ListTrustedDevices {
    listTrustedDevices {
      deviceKey
      deviceName
      deviceCreateDate
      deviceLastModifiedDate
      deviceLastAuthenticatedDate
    }
  }
`;

export interface TrustedDevice {
  deviceKey: string;
  deviceName: string;
  deviceCreateDate: string | null;
  deviceLastModifiedDate: string | null;
  deviceLastAuthenticatedDate: string | null;
}

export interface ListTrustedDevicesQueryData {
  listTrustedDevices: TrustedDevice[];
}

export const useListTrustedDevicesQuery = (
  options?: QueryHookOptions<ListTrustedDevicesQueryData>
) => useQuery<ListTrustedDevicesQueryData>(LIST_TRUSTED_DEVICES_QUERY, options);

/**
 * SETUP MFA MUTATION
 */
export const SETUP_MFA_MUTATION = gql`
  mutation SetupMFA($mfaSession: String) {
    setupMFA(mfaSession: $mfaSession) {
      qrCodeUrl
      secretKey
      setupComplete
    }
  }
`;

export interface SetupMFAMutationData {
  setupMFA: {
    qrCodeUrl: string;
    secretKey: string;
    setupComplete: boolean;
  };
}

export interface SetupMFAMutationVars {
  mfaSession?: string;
}

export const useSetupMFAMutation = (
  options?: MutationHookOptions<SetupMFAMutationData, SetupMFAMutationVars>
) =>
  useMutation<SetupMFAMutationData, SetupMFAMutationVars>(
    SETUP_MFA_MUTATION,
    options
  );

export const setupMFA = async (vars?: SetupMFAMutationVars) =>
  client.mutate<SetupMFAMutationData, SetupMFAMutationVars>({
    mutation: SETUP_MFA_MUTATION,
    variables: vars,
  });

/**
 * VERIFY MFA SETUP MUTATION
 */
export const VERIFY_MFA_SETUP_MUTATION = gql`
  mutation VerifyMFASetup($code: String!, $mfaSession: String) {
    verifyMFASetup(code: $code, mfaSession: $mfaSession) {
      success
      sessionTokens {
        access
        refresh
        id
      }
      requiresSetup
    }
  }
`;

export interface VerifyMFASetupMutationData {
  verifyMFASetup: {
    success: boolean;
    sessionTokens?: {
      access: string;
      refresh: string;
      id: string;
    } | null;
    requiresSetup?: boolean;
  };
}

export interface VerifyMFASetupMutationVars {
  code: string;
  mfaSession?: string;
}

export const useVerifyMFASetupMutation = (
  options?: MutationHookOptions<
    VerifyMFASetupMutationData,
    VerifyMFASetupMutationVars
  >
) =>
  useMutation<VerifyMFASetupMutationData, VerifyMFASetupMutationVars>(
    VERIFY_MFA_SETUP_MUTATION,
    options
  );

export const verifyMFASetup = async (body: VerifyMFASetupMutationVars) =>
  client.mutate<VerifyMFASetupMutationData, VerifyMFASetupMutationVars>({
    mutation: VERIFY_MFA_SETUP_MUTATION,
    variables: body,
  });

/**
 * VERIFY MFA MUTATION
 */
export const VERIFY_MFA_MUTATION = gql`
  mutation VerifyMFA($username: String!, $code: String!, $session: String!) {
    verifyMFA(username: $username, code: $code, session: $session) {
      success
      sessionTokens {
        access
        refresh
        id
      }
      requiresSetup
    }
  }
`;

export interface SessionTokens {
  access: string;
  refresh: string;
  id: string;
}

export interface VerifyMFAMutationData {
  verifyMFA: {
    success: boolean;
    sessionTokens?: SessionTokens;
    requiresSetup?: boolean;
  };
}

export interface VerifyMFAMutationVars {
  username: string;
  code: string;
  session: string;
}

export const useVerifyMFAMutation = (
  options?: MutationHookOptions<VerifyMFAMutationData, VerifyMFAMutationVars>
) =>
  useMutation<VerifyMFAMutationData, VerifyMFAMutationVars>(
    VERIFY_MFA_MUTATION,
    options
  );

export const verifyMFA = async (body: VerifyMFAMutationVars) =>
  client.mutate<VerifyMFAMutationData, VerifyMFAMutationVars>({
    mutation: VERIFY_MFA_MUTATION,
    variables: body,
  });

/**
 * FORGET TRUSTED DEVICE MUTATION
 */
export const FORGET_TRUSTED_DEVICE_MUTATION = gql`
  mutation ForgetTrustedDevice($deviceKey: String!) {
    forgetTrustedDevice(deviceKey: $deviceKey)
  }
`;

export interface ForgetTrustedDeviceMutationData {
  forgetTrustedDevice: boolean;
}

export interface ForgetTrustedDeviceMutationVars {
  deviceKey: string;
}

export const useForgetTrustedDeviceMutation = (
  options?: MutationHookOptions<
    ForgetTrustedDeviceMutationData,
    ForgetTrustedDeviceMutationVars
  >
) =>
  useMutation<ForgetTrustedDeviceMutationData, ForgetTrustedDeviceMutationVars>(
    FORGET_TRUSTED_DEVICE_MUTATION,
    options
  );

export const forgetTrustedDevice = async (
  body: ForgetTrustedDeviceMutationVars
) =>
  client.mutate<
    ForgetTrustedDeviceMutationData,
    ForgetTrustedDeviceMutationVars
  >({
    mutation: FORGET_TRUSTED_DEVICE_MUTATION,
    variables: body,
  });

/**
 * DISABLE MFA MUTATION
 */
export const DISABLE_MFA_MUTATION = gql`
  mutation DisableMFA {
    disableMFA
  }
`;

export interface DisableMFAMutationData {
  disableMFA: boolean;
}

export const useDisableMFAMutation = (
  options?: MutationHookOptions<DisableMFAMutationData>
) => useMutation<DisableMFAMutationData>(DISABLE_MFA_MUTATION, options);

export const disableMFA = async () =>
  client.mutate<DisableMFAMutationData>({
    mutation: DISABLE_MFA_MUTATION,
  });
